import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {Helmet} from 'react-helmet'
import {
    useAccessToken,
    useCategory,
    useCustomerId,
    useProductSearch,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Components
import {
    Box,
    Flex,
    SimpleGrid,
    Grid,
    Select,
    Text,
    FormControl,
    Stack,
    useDisclosure,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalContent,
    ModalCloseButton,
    ModalOverlay,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Img,
    FormLabel
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Switch, Spinner} from '@chakra-ui/react'

import Pagination from '../../components/pagination'

// Project Components
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'
import Refinements from './partials/refinements'
import CategoryLinks from './partials/category-links'
import EmptySearchResults from '@salesforce/retail-react-app/app/pages/product-list/partials/empty-results'
import PageHeader from './partials/page-header'
import AbovePageHeader from '@salesforce/retail-react-app/app/pages/product-list/partials/above-page-header'
import PageDesignerPromotionalBanner from '@salesforce/retail-react-app/app/pages/product-list/partials/page-designer-promotional-banner'

// Hooks
import {useSortUrls, useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {usePageUrls} from '../../hooks/use-page-urls'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'

// Others
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {InformationIcon} from '../../components/icons'

// Constants
import {
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
    TOAST_MESSAGE_REMOVED_FROM_WISHLIST,
    STALE_WHILE_REVALIDATE,
    PRODUCT_LIST_IMAGE_VIEW_TYPE,
    PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID,
    DEFAULT_SEARCH_PARAMS
} from '@salesforce/retail-react-app/app/constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import LoadingSpinner from '../../components/loading-spinner'
import {useWishList} from '../../hooks/use-wish-list'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useProductSearchFetcher} from '../../hooks/use-product-search-fetcher'
import {
    resultsCountStyles,
    availableAtStoreStyle,
    adjustFiltersStyles,
    informationIconStyles,
    plpAboveFooter,
    size1x1,
    size2x1
} from './index.style'
import SelectedRefinements from './partials/selected-refinements'
import {rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'
import StoreSelectorModal from '../../components/store-selector-modal'
import {useSearchStores} from '@salesforce/commerce-sdk-react'
import useSitePreference from '../../hooks/use-site-preference'

import useUpdateShopperContext from '../../components/store-selector-modal/updateShopperContext'
import StoreDetails from '../../components/store-details'
import useCustomFetch from '../../hooks/useFetchContent'
import useFetchLibraryFolders from '../../hooks/useFetchLibrary'
import RelatedArticles from '../../components/related-articles'
import {getUrlSearchParam} from '../../utills/url'
import NoSearchResults from './partials/no-search-results'
import CategoryLanding from './partials/category-landing'
import CategoryContentLanding from '../category/partials/category-landing'
import BrandDetail from '../brand-detail'
import PageNotFound from '../page-not-found'
import ContentRenderer from '../../components/content-renderer'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']

const ProductListsSkelton = ({limit}) => {
    return new Array(limit).fill(0).map((value, index) => <ProductTileSkeleton key={index} />)
}

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = (props) => {
    // Using destructuring to omit properties; we must rename `isLoading` because we use a different
    // `isLoading` later in this function.

    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {
        isOpen: isOpenStoreSelector,
        onOpen: onOpenStoreSelector,
        onClose: onCloseStoreSelector
    } = useDisclosure()
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const history = useHistory()
    const params = useParams()
    const location = useLocation()
    const toast = useToast()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const customerId = useCustomerId()
    const intl = useIntl()
    // const {data: sitePrefData} = useSitePreference('productTileFetchLimit')
    const {fetchProductSearchResults} = useProductSearchFetcher()
    const [isNextPageLoading, setNextPageloading] = useState(false)
    const [errorInNextPageLoading, setErrorInNextPageLoading] = useState(false)
    const [isProductsLoading, setProductsLoading] = useState(false)

    const [statesWithStores, setStatesWithStores] = useState([])
    const {data} = useSitePreference('statesMapping,storeLookupMaxDistance')
    const statesList = data?.statesMapping
    const {data: defaultLocationData} = useSitePreference('defaultStoreLatLong')
    const [defaultLatLng, setDefaultLatLng] = useState({lat: null, lng: null})

    const [isStoreModalFromFilter, setIsStoreModalFromFilter] = useState(false)

    /**************** Page State ****************/
    const [filtersLoading, setFiltersLoading] = useState(false)
    const [wishlistLoading, setWishlistLoading] = useState([])
    const [sortOpen, setSortOpen] = useState(false)

    const {shopperContext} = useUpdateShopperContext()

    const {data: sitePrefData} = useSitePreference('enableCategoryBillBoards')
    const {data: limitSizePrefData} = useSitePreference('productTileFetchLimit')
    const storeDetails = useMemo(() => {
        if (shopperContext?.customQualifiers?.storeDetails) {
            return JSON.parse(shopperContext.customQualifiers.storeDetails)
        }
        return null
    }, [shopperContext])
    const {app: appConfig} = getConfig()

    useEffect(() => {
        if (defaultLocationData?.defaultStoreLatLong) {
            try {
                const parsed = JSON.parse(defaultLocationData.defaultStoreLatLong)
                setDefaultLatLng({lat: parsed.lat, lng: parsed.lng})
            } catch (err) {
                console.error('Invalid defaultStoreLatLong format', err)
            }
        }
    }, [defaultLocationData])

    const [storeSearchParameters, setStoreSearchParameters] = useState(null)
    const [enableSearchStore, setEnableSearchStore] = useState(false)

    useEffect(() => {
        if (defaultLatLng.lat != null && defaultLatLng.lng != null) {
            setStoreSearchParameters({
                countryCode: 'US',
                latitude: defaultLatLng.lat,
                longitude: defaultLatLng.lng,
                distanceUnit: 'mi',
                limit: 200
            })
            setEnableSearchStore(true)
        }
    }, [defaultLatLng])

    const {data: storeData} = useSearchStores(
        {
            parameters: storeSearchParameters
        },
        {
            enabled: enableSearchStore
        }
    )

    useEffect(() => {
        if (storeData?.data && statesList) {
            const storeStatesSet = new Set(storeData.data.map((store) => store.stateCode))
            const filteredStates = Object.entries(statesList)
                .filter(([code]) => storeStatesSet.has(code))
                .map(([code, name]) => ({code, name}))
            setStatesWithStores(filteredStates)
        }
    }, [storeData, statesList])

    const urlParams = new URLSearchParams(location.search)
    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery

    /***************** Related Articles *****************/
    const [articlesPage, setArticlesPage] = useState(0)
    const [showLoadMore, setShowLoadMore] = useState(false)
    const articlesPerPage = 6
    const articlesStart = articlesPage * articlesPerPage
    const [allArticles, setAllArticles] = useState([])

    /**************** Mutation Actions ****************/
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const {
        error,
        isError: isCategoryError,
        data: category
    } = useCategory(
        {
            parameters: {
                id: params.categoryId,
                locale: 'default'
            }
        },
        {
            enabled: !isSearch && !!params.categoryId
        }
    )

    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()

    if (params.categoryId) {
        searchParams._refine.push(`cgid=${params.categoryId}`)
    }

    for (const key of urlParams.keys()) {
        if (key.startsWith('prefn')) {
            const idx = key.replace('prefn', '')
            const prefvKey = `prefv${idx}`
            const name = urlParams.get(key)
            const val = urlParams.get(prefvKey)
            if (name && val) {
                searchParams._refine.push(`${name}=${val}`)

                const existing = searchParams.refine[name]
                if (!existing) {
                    searchParams.refine[name] = val
                } else if (Array.isArray(existing)) {
                    searchParams.refine[name] = [...existing, val]
                } else {
                    searchParams.refine[name] = `${existing},${val}`
                }
            }
        }
    }

    if (location.search.indexOf('prefn') !== -1) {
        searchParams.isBrandSearch = 'true'
    }
    if (!searchParams.sort || searchParams.sort === undefined) {
        searchParams.sort = appConfig.defaultSortingOption || category.c_defaultSortingRule
    }

    // remove original bookmarked params so they don't get serialized again
    Array.from(urlParams.keys()).forEach((k) => {
        if (k.startsWith('prefn') || k.startsWith('prefv')) {
            if (searchParams[k] !== undefined) {
                delete searchParams[k]
            }
        }
    })

    // Use the search query for related articles search only if isSearch is true
    const {data: relatedArticlesData, loading} = isSearch
        ? useFetchLibraryFolders(
              `content_search?count=${articlesPerPage}&q=${encodeURIComponent(
                  searchQuery
              )}&start=${articlesStart}`
          )
        : {data: null, loading: false}

    useEffect(() => {
        const hits = relatedArticlesData?.hits

        if (hits) {
            setAllArticles((prev) => [...prev, ...hits])
            setShowLoadMore(hits.length === articlesPerPage)
        }
    }, [relatedArticlesData])

    const loadMoreArticles = () => {
        setArticlesPage((prevPage) => prevPage + 1)
    }

    /**************** Query Actions ****************/
    // _refine is an invalid param for useProductSearch, we don't want to pass it to API call
    const {_refine, products: productsParam, offset, ...restOfParams} = searchParams
    const updatedParams = _refine.map((param) => {
        const [key, value] = param.split('=')
        if (key) {
            const decoded = decodeURIComponent(value)
            const joined = decoded.split(',').join('|')
            return `${key}=${joined}`
        }
        return param
    })

    const updatedRefine = Object.entries(searchParams.refine).reduce((acc, [key, value]) => {
        if (typeof value === 'string') {
            acc[key] = value.split(',').map((item) => item.trim())
        } else {
            acc[key] = value
        }
        return acc
    }, {})

    const parameters = {
        ...restOfParams,
        limit:
            productsParam ||
            (limitSizePrefData?.productTileFetchLimit < 200
                ? limitSizePrefData?.productTileFetchLimit
                : DEFAULT_SEARCH_PARAMS.limit), // Map 'products' URL param to 'limit' API param
        offset: offset, // Always use offset 0
        perPricebook: true,
        allVariationProperties: true,
        allImages: true,
        expand: ['promotions', 'variations', 'prices', 'images', 'custom_properties'],
        refine: updatedParams,
        locale: 'default'
    }

    const {
        isLoading,
        isFetched,
        isRefetching,
        error: productError,
        data: productSearchResult
    } = useProductSearch(
        {
            parameters
        },
        {
            keepPreviousData: true,
            staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh for 5 min
            cacheTime: 30 * 60 * 1000 // 30 minutes - keep in cache for 30 min
        }
    )
    var searchLocation = productSearchResult?.searchPhraseSuggestions?.c_redirectURL
    if (searchLocation) {
        if (searchLocation?.startsWith('http') || searchLocation?.startsWith('https')) {
            if (typeof window !== 'undefined') {
                window.location.href = searchLocation
            } else {
                console.warn('External redirect attempted on server:', searchLocation)
            }
        } else {
            navigate(searchLocation)
        }
        return
    }

    const [products, setProducts] = useState(productSearchResult?.hits || [])
    const [isFilterStoreAvailable, setIsFilterStoreAvailable] = useState(
        !!productSearchResult?.selectedRefinements?.c_availableStores || false
    )

    // Apply disallow list to refinements.
    if (productSearchResult?.refinements) {
        productSearchResult.refinements = productSearchResult.refinements.filter(
            ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
        )
    }

    const [isPageLoading, setIsPageLoading] = useState(true)

    const {suggestedSearchQuery, showNoSearchResults} = useMemo(() => {
        const suggestions = productSearchResult?.searchPhraseSuggestions?.suggestedPhrases ?? []
        const phrase = suggestions[0]?.phrase ?? ''
        const hasValidMatch = suggestions.some((s) => s?.phrase)

        return {
            suggestedSearchQuery: phrase,
            showNoSearchResults: isSearch && !hasValidMatch
        }
    }, [productSearchResult, isSearch])

    useEffect(() => {
        setIsPageLoading(true)
    }, [location.search])

    useEffect(() => {
        if (!isLoading && productSearchResult) {
            setIsPageLoading(false)
        }
    }, [isLoading, productSearchResult])

    /**************** Response Handling ****************/
    if (res) {
        res.set('Cache-Control', 'public, s-maxage=300, stale-while-revalidate=30')
    }

    useEffect(() => {
        const {snitizeUrlSearchParam, hasProductParams} = getUrlSearchParam(location.search)
        const newUrl = `${location.pathname}${snitizeUrlSearchParam && snitizeUrlSearchParam}`
        hasProductParams && history.replace(newUrl)
    }, [])

    // Reset scroll position when `isRefetching` becomes `true`.
    useEffect(() => {
        // isRefetching && window.scrollTo(0, 0)
        setFiltersLoading(isRefetching)
        setNextPageloading(false)
        setProductsLoading(isRefetching)
    }, [isRefetching])

    useEffect(() => {
        setProducts(productSearchResult?.hits)
        setProductsLoading(false)
    }, [productSearchResult?.hits])

    /**************** Render Variables ****************/
    const basePath = `${location.pathname}${location.search}`
    const showNoResults =
        !isLoading && !isProductsLoading && productSearchResult && !(products && products.length)
    const {total, sortingOptions} = productSearchResult || {}
    const currentLimit = parameters?.limit || 30
    const hasNextPage = total > currentLimit
    const selectedSortingOptionLabel =
        sortingOptions?.find(
            (option) =>
                option.id === productSearchResult?.selectedSortingOption ||
                option.id === appConfig.defaultSortingOption
        ) ?? sortingOptions?.[0]

    // Track load more analytics when limit changes
    useEffect(() => {
        const defaultLimit =
            limitSizePrefData?.productTileFetchLimit < 200
                ? limitSizePrefData?.productTileFetchLimit
                : DEFAULT_SEARCH_PARAMS.limit
        if (currentLimit > defaultLimit && productSearchResult?.hits && category) {
            setLoadMore(currentLimit, productSearchResult.hits)
        }
    }, [currentLimit, productSearchResult?.hits])

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [isProductsLoading])

    // Get urls to be used for pagination, page size changes, and sorting.
    const pageUrls = usePageUrls({total})
    const sortUrls = useSortUrls({options: sortingOptions})
    /**************** Action Handlers ****************/
    const {data: wishlist} = useWishList()
    const addItemToWishlist = async (product) => {
        setWishlistLoading([...wishlistLoading, product.productId])

        // TODO: This wishlist object is from an old API, we need to replace it with the new one.
        const listId = wishlist.id
        await createCustomerProductListItem(
            {
                parameters: {customerId, listId},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: product.productId
                }
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                        status: 'success',
                        action: (
                            // it would be better if we could use <Button as={Link}>
                            // but unfortunately the Link component is not compatible
                            // with Chakra Toast, since the ToastManager is rendered via portal
                            // and the toast doesn't have access to intl provider, which is a
                            // requirement of the Link component.
                            <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                                {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                            </Button>
                        )
                    })
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
                }
            }
        )
    }

    const removeItemFromWishlist = async (product) => {
        setWishlistLoading([...wishlistLoading, product.productId])

        const listId = wishlist.id
        const itemId = wishlist.customerProductListItems.find(
            (i) => i.productId === product.productId
        ).id

        await deleteCustomerProductListItem(
            {
                body: {},
                parameters: {customerId, listId, itemId}
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST),
                        status: 'success'
                    })
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
                }
            }
        )
    }

    // Sets the filter change in the dataLayer for analytics tracking
    const setFilterChange = (filterName, filterValue) => {
        if (filterName && filterValue) {
            if (typeof window !== 'undefined' && window?.dataLayer) {
                window.dataLayer.push({
                    event: 'categoryRefinement',
                    eventCategory: 'Filter',
                    eventAction: filterName,
                    eventLabel: filterValue
                })
            }
        }
    }
    // Toggles filter on and off
    const toggleFilter = (value, attributeId, selected, allowMultiple = true) => {
        const searchParamsCopy = {...searchParams}
        if (!selected) setFilterChange(attributeId, value.value)
        // Reset products count to default when filters change
        delete searchParamsCopy.limit
        delete searchParamsCopy.offset
        searchParamsCopy.products =
            limitSizePrefData?.productTileFetchLimit < 200
                ? limitSizePrefData?.productTileFetchLimit
                : DEFAULT_SEARCH_PARAMS.limit

        // If we aren't allowing for multiple selections, simply clear any value set for the
        // attribute, and apply a new one if required.
        if (!allowMultiple) {
            const previousValue = searchParamsCopy.refine[attributeId]
            delete searchParamsCopy.refine[attributeId]

            // Note the loose comparison, for "string != number" checks.
            if (!selected && value.value != previousValue) {
                searchParamsCopy.refine[attributeId] = value.value
            }
        } else {
            // Get the attibute value as an array.
            let attributeValue = searchParamsCopy.refine[attributeId] || []

            // Ensure that the value is still converted into an array if it's a `string` or `number`.
            if (typeof attributeValue === 'string') {
                attributeValue = attributeValue.split('|')
            } else if (typeof attributeValue === 'number') {
                attributeValue = [attributeValue]
            }

            // Either set the value, or filter the value out.
            if (!selected) {
                attributeValue.push(value.value)
            } else {
                // Note the loose comparison, for "string != number" checks.
                attributeValue = attributeValue[0]?.split(',')?.filter((v) => v != value.value)
            }

            // Update the attribute value in the new search params.
            searchParamsCopy.refine[attributeId] = attributeValue
            Object.keys(searchParamsCopy.refine).map((attrKeys) => {
                searchParamsCopy.refine[attrKeys] = encodeURIComponent(
                    searchParamsCopy.refine[attrKeys]
                )
            })

            // If the update value is an empty array, remove the current attribute key.
            if (searchParamsCopy.refine[attributeId].length === 0) {
                delete searchParamsCopy.refine[attributeId]
            }
        }

        const urlParamsLocal = new URLSearchParams(location.search)
        const isBrandSearchParam =
            (
                urlParamsLocal.get('isBrandSearch') ??
                searchParamsCopy?.isBrandSearch ??
                ''
            ).toString() === 'true'

        if (isSearch || isBrandSearchParam) {
            navigate(`/search?${stringifySearchParams(searchParamsCopy)}`)
        } else {
            navigate(`/shop/${params.categoryId}?${stringifySearchParams(searchParamsCopy)}`)
        }
    }

    // Clears all filters
    const resetFilters = () => {
        let refine = {}
        if (searchParams.refine?.c_availableStores) {
            refine = {c_availableStores: searchParams.refine.c_availableStores}
        }
        const newSearchParams = {
            ...searchParams,
            refine:
                typeof searchParams.refine === 'object' &&
                !Array.isArray(searchParams.refine) &&
                searchParams.refine?.c_availableStores
                    ? {c_availableStores: searchParams.refine.c_availableStores}
                    : {}
        }
        delete newSearchParams.limit
        delete newSearchParams.offset
        const newPath = isSearch
            ? `/search?${stringifySearchParams(newSearchParams)}`
            : `/shop/${params.categoryId}?${stringifySearchParams(newSearchParams)}`

        navigate(newPath)
    }

    useEffect(() => {
        if ((isFilterStoreAvailable || isStoreModalFromFilter) && storeDetails?.id) {
            toggleFilter({value: storeDetails?.id}, 'c_availableStores', false, false)
        }
    }, [storeDetails?.id])

    useEffect(() => {
        if (
            isFilterStoreAvailable !== !!productSearchResult?.selectedRefinements?.c_availableStores
        ) {
            setIsFilterStoreAvailable(!!productSearchResult?.selectedRefinements?.c_availableStores)
        }
    }, [productSearchResult])

    const filterStoreAvailable = (event) => {
        if (!storeDetails?.id) {
            setIsStoreModalFromFilter(true)
            onOpenStoreSelector()
        } else {
            setIsFilterStoreAvailable(event.target.checked)
            toggleFilter(
                {value: storeDetails?.id},
                'c_availableStores',
                !event.target.checked,
                false
            )
        }
    }

    const openStoreModal = () => {
        setIsStoreModalFromFilter(false)
        onOpenStoreSelector()
    }

    const getVisibleProductsData = (products, category, currentLimit) => {
        if (!products || !Array.isArray(products) || !category || !category.parentCategoryTree) {
            return []
        }
        const defaultLimit =
            limitSizePrefData?.productTileFetchLimit < 200
                ? limitSizePrefData?.productTileFetchLimit
                : DEFAULT_SEARCH_PARAMS.limit
        const previouslyLoadedCount = currentLimit - defaultLimit
        return products.map((product, index) => ({
            name: product.productName,
            id: product.productId,
            list: category.parentCategoryTree.map((cat) => cat.name).join('/'),
            category: `Category L${category.parentCategoryTree.length}`,
            position: index + 1 + previouslyLoadedCount
        }))
    }

    // Sets the load more event in the dataLayer for analytics tracking
    const setLoadMore = (newLimit, newProducts) => {
        if (newLimit && newProducts && newProducts.length && category) {
            if (typeof window !== 'undefined' && window?.dataLayer) {
                window.dataLayer.push({
                    event: 'loadMoreProducts',
                    limit: newLimit,
                    itemsLoaded: getVisibleProductsData(newProducts, category, newLimit)
                })
            }
        }
    }
    const loadMore = async () => {
        setNextPageloading(true)
        try {
            const defaultLimit =
                limitSizePrefData?.productTileFetchLimit < 200
                    ? limitSizePrefData?.productTileFetchLimit
                    : DEFAULT_SEARCH_PARAMS.limit
            const newLimit = currentLimit + defaultLimit
            const searchParamsCopy = {...searchParams}
            // Remove limit, offset from params and use 'products' instead
            delete searchParamsCopy.limit
            delete searchParamsCopy.offset
            searchParamsCopy.products = newLimit

            // Update URL with new products count - this will trigger useProductSearch to refetch with cache
            const newPath = isSearch
                ? `/search?${stringifySearchParams(searchParamsCopy)}`
                : `/shop/${params.categoryId}?${stringifySearchParams(searchParamsCopy)}`

            history.replace(newPath)
        } catch (error) {
            setErrorInNextPageLoading(true)
        } finally {
            setNextPageloading(false)
        }
    }

    const hasSidebarContent = useMemo(() => {
        const hasSelected = !!(
            Object.keys(productSearchResult?.selectedRefinements || {})?.length > 0
        )
        const hasAnyBucket = !!(productSearchResult?.refinements?.length > 0)
        return hasSelected || hasAnyBucket
    }, [productSearchResult])

    // Returns an string where each key is a search param, and if the key is 'refine', it collects all refine params as an array.
    const previousSearchParams = useMemo(() => {
        const {snitizeUrlSearchParam} = getUrlSearchParam(location.search)
        return snitizeUrlSearchParam
    }, [productSearchResult])

    const categoryBillboardData = useMemo(() => {
        try {
            if (!category.c_billBoardConfig) return {}
            return JSON.parse(category.c_billBoardConfig)
        } catch (error) {
            return {}
        }
    }, [category])

    /**************** Einstein ****************/
    useEffect(() => {
        if (productSearchResult) {
            if (isSearch) {
                try {
                    einstein.sendViewSearch(searchQuery, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewSearch error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, searchQuery}
                    })
                }
                activeData.sendViewSearch(searchParams, productSearchResult)
            } else {
                try {
                    einstein.sendViewCategory(category, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewCategory error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, category}
                    })
                }
                activeData.sendViewCategory(searchParams, category, productSearchResult)
            }
        }
    }, [productSearchResult])

    const {token} = useAccessToken()
    const {response} = useCustomFetch(token, `clp-bottom-slot:category:${category?.id}`, true)

    useEffect(() => {
        return () => {
            setProducts([])
        }
    }, [params.categoryId])

    /**************** Analytics ****************/

    // Page View Context:
    useEffect(() => {
        if (typeof window !== 'undefined' && productSearchResult) {
            window.dataLayer = window.dataLayer || []
            const tagParams = {
                ecomm_pagetype: isSearch ? 'searchresults' : 'category',
                ecomm_prodid: Array.isArray(productSearchResult?.hits)
                    ? productSearchResult.hits.map((hit) => hit.productId)
                    : []
            }

            if (!isSearch) {
                tagParams.ecomm_category = params.categoryId || ''
            }

            window.dataLayer.push({
                'page-type': isSearch ? 'search results' : 'category',
                google_tag_params: tagParams
            })
        }
    }, [productSearchResult, params.categoryId, isSearch])

    useEffect(() => {
        if (typeof window !== 'undefined' && isSearch && searchQuery) {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({event: 'search', searchTerm: searchQuery})
        }
    }, [isSearch, searchQuery])

    // Product List Event:
    useEffect(() => {
        if (typeof window !== 'undefined' && productSearchResult && window?.dataLayer) {
            window.dataLayer.push({event: 'productListEvent'})
        }
    }, [productSearchResult])

    // Product Click
    const setProductDataForClick = (product) => {
        if (!productSearchResult || !Array.isArray(productSearchResult.hits)) {
            return null
        }
        let categoryName = ''
        let categoryLevel = ''
        if (category && Array.isArray(category.parentCategoryTree)) {
            categoryName = category.parentCategoryTree.map((cat) => cat.name).join('/')
            categoryLevel = `Category L${category.parentCategoryTree.length}`
        }
        const productDetails = {
            name: product.productName,
            id: product.productId,
            price: product.price,
            position:
                productSearchResult.hits.findIndex((hit) => hit.productId === product.productId) +
                1,
            category: isSearch ? '' : categoryName
        }
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({ecommerce: null})
            window.dataLayer.push({
                ecommerce: {
                    click: {
                        actionField: {
                            list: isSearch ? `Search Results ${searchQuery}` : categoryLevel
                        },
                        products: [productDetails]
                    }
                },
                event: 'productClick'
            })
        }
    }

    // Track impressions for only visible products using Intersection Observer
    const handleVisibilityChange = (() => {
        let timeout = null
        let visibleIds = new Set()

        return function (id, isVisible) {
            if (isVisible) {
                visibleIds.add(id)
            } else {
                visibleIds.delete(id)
            }
            if (timeout) clearTimeout(timeout)
            timeout = setTimeout(() => {
                if (
                    typeof window !== 'undefined' &&
                    window?.dataLayer &&
                    productSearchResult &&
                    (isSearch || (category && Array.isArray(category.parentCategoryTree)))
                ) {
                    const productsToTrack = productSearchResult.hits.filter((hit) =>
                        visibleIds.has(hit.productId)
                    )
                    if (productsToTrack.length) {
                        const impressions = productsToTrack.map((product) => ({
                            name: product.productName,
                            id: product.productId,
                            list: isSearch
                                ? `Search Results ${searchQuery}`
                                : category.parentCategoryTree.map((cat) => cat.name).join('/'),
                            position:
                                productSearchResult.hits.findIndex(
                                    (hit) => hit.productId === product.productId
                                ) + 1,
                            category: isSearch
                                ? ''
                                : `Category L${category.parentCategoryTree.length}`
                        }))
                        window.dataLayer.push({ecommerce: null})
                        window.dataLayer.push({
                            ecommerce: {impressions: impressions},
                            event: 'impressions'
                        })
                    } else {
                        window.dataLayer.push({ecommerce: null})
                    }
                }
            }, 300)
        }
    })()

    if (productError?.response?.status === 400) {
        return <PageNotFound />
    }

    if (isCategoryError) {
        const errorStatus = error?.response?.status
        if (errorStatus === 404 || error?.message?.includes('not found')) {
            return <PageNotFound fullScreen={true} />
        }
        return <PageNotFound fullScreen={true} />
    }

    return (
        <Box
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            layerStyle="page"
            paddingTop={{base: 6, lg: 8}}
            paddingLeft={[pxToRem(11), pxToRem(11), pxToRem(45), pxToRem(45)]}
            paddingRight={[pxToRem(11), pxToRem(11), pxToRem(45), pxToRem(45)]}
            maxWidth={pxToRem(1440)}
            {...rest}
        >
            <Helmet>
                <title>{category?.pageTitle ?? category?.name ?? searchQuery}</title>
                <meta name="description" content={category?.pageDescription ?? searchQuery} />
                <meta name="keywords" content={category?.pageKeywords} />
            </Helmet>
            {showNoSearchResults && !params?.categoryId && !isLoading ? (
                <NoSearchResults
                    searchQuery={searchQuery}
                    suggestedSearchQuery={suggestedSearchQuery}
                />
            ) : isLoading ? (
                <LoadingSpinner />
            ) : null}

            {showNoResults && !hasSidebarContent ? (
                <EmptySearchResults searchQuery={searchQuery} category={category} />
            ) : (
                <>
                    <AbovePageHeader />
                    <PageDesignerPromotionalBanner />
                    {/* Header */}
                    {/* Imported PageHeader which includes CategoryHeader */}
                    {!showNoSearchResults && !isPageLoading && (
                        <>
                            <PageHeader
                                searchQuery={searchQuery}
                                articlesCount={relatedArticlesData?.total}
                                category={category}
                                productSearchResult={productSearchResult}
                                isLoading={isLoading}
                                selectedRefinements={productSearchResult?.selectedRefinements || {}}
                            />
                            {category?.c_renderingTemplate !== 'rendering/category/catlanding' &&
                                category?.c_renderingTemplate !==
                                    'rendering/category/categorycontentlanding' &&
                                category?.c_renderingTemplate !==
                                    'rendering/category/dynamiccategorylanding' && (
                                    <Flex align="flex-end" mt={5} mb={9}>
                                        <HideOnDesktop>
                                            <Button
                                                variant="red-link-uline"
                                                onClick={onOpen}
                                                marginRight="auto"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Filter & Sort >>"
                                                    id="product_list.button.filter_and_sort"
                                                />
                                            </Button>
                                        </HideOnDesktop>
                                        <Box sx={resultsCountStyles}>
                                            <FormattedMessage
                                                defaultMessage="{count} Results"
                                                id="product_list.button.count.results"
                                                values={{count: productSearchResult?.total}}
                                            />
                                        </Box>
                                    </Flex>
                                )}
                        </>
                    )}

                    {category?.c_renderingTemplate === 'rendering/category/catlanding' && (
                        <CategoryLanding category={category} />
                    )}
                    {category?.c_renderingTemplate ===
                        'rendering/category/categorycontentlanding' && (
                        <CategoryContentLanding category={category} />
                    )}
                    {category?.c_renderingTemplate ===
                        'rendering/category/dynamiccategorylanding' && (
                        <BrandDetail category={category} />
                    )}
                    {/* Body  */}
                    {!showNoSearchResults &&
                        category?.c_renderingTemplate !== 'rendering/category/catlanding' &&
                        category?.c_renderingTemplate !==
                            'rendering/category/categorycontentlanding' &&
                        category?.c_renderingTemplate !==
                            'rendering/category/dynamiccategorylanding' && (
                            <Grid
                                templateColumns={{base: '1fr', md: `${pxToRem(298)} 1fr`}}
                                columnGap={12}
                                mb={{base: pxToRem(60), md: pxToRem(80)}}
                            >
                                <Stack display={{base: 'none', md: 'flex'}}>
                                    {/* selected refinements */}
                                    <Box mb="2">
                                        <SelectedRefinements
                                            filters={productSearchResult?.refinements}
                                            toggleFilter={toggleFilter}
                                            handleReset={resetFilters}
                                            selectedFilterValues={
                                                productSearchResult?.selectedRefinements
                                            }
                                        />
                                    </Box>
                                    {/* sort by */}
                                    <Box className="mt-0">
                                        <Sort
                                            sortUrls={sortUrls}
                                            productSearchResult={productSearchResult}
                                            basePath={basePath}
                                            defaultSort={searchParams.sort}
                                        />
                                    </Box>

                                    {/* my store */}
                                    <Box>
                                        <Box marginTop={pxToRem(26)}>
                                            {/* available at my store toggle button */}
                                            <FormControl display="flex" alignItems="center">
                                                <Switch
                                                    id="store-available_dt"
                                                    isChecked={isFilterStoreAvailable}
                                                    onChange={filterStoreAvailable}
                                                />
                                                <FormLabel
                                                    sx={availableAtStoreStyle}
                                                    htmlFor="store-available_dt"
                                                    mb="0"
                                                >
                                                    {intl.formatMessage({
                                                        id: 'product_list.availableAtMyStore.label',
                                                        defaultMessage: 'Available At My Store'
                                                    })}
                                                </FormLabel>
                                            </FormControl>

                                            <Box
                                                display={'flex'}
                                                alignItems="flex-start"
                                                marginTop={pxToRem(20)}
                                            >
                                                {/* selected store details */}
                                                <StoreDetails isPLP={true} />
                                                {/* avaialable at store ends here */}
                                                {/* change store button */}
                                                <Button
                                                    variant="red-link-uline"
                                                    onClick={openStoreModal}
                                                    mt={pxToRem(2)}
                                                    mb={pxToRem(15)}
                                                    fontWeight={500}
                                                    fontSize={{base: '10px', md: '12px'}}
                                                    color="cav_calltoaction_darkred"
                                                    lineHeight="normal"
                                                    letterSpacing={pxToRem(1)}
                                                >
                                                    {storeDetails &&
                                                    Object.keys(storeDetails)?.length
                                                        ? intl.formatMessage({
                                                              id: 'product_list.store.change.label',
                                                              defaultMessage: 'Change'
                                                          })
                                                        : intl.formatMessage({
                                                              id: 'product_list.store.selectSore.label',
                                                              defaultMessage: 'Select a store'
                                                          })}
                                                </Button>
                                            </Box>
                                        </Box>

                                        {/* store selector modal */}
                                        <StoreSelectorModal
                                            isOpen={isOpenStoreSelector}
                                            onClose={onCloseStoreSelector}
                                            states={statesWithStores}
                                        />
                                    </Box>

                                    <Refinements
                                        itemsBefore={
                                            category?.categories
                                                ? [
                                                      <CategoryLinks
                                                          key="itemsBefore"
                                                          category={category}
                                                      />
                                                  ]
                                                : undefined
                                        }
                                        excludedFilters={[]}
                                        isLoading={filtersLoading}
                                        toggleFilter={toggleFilter}
                                        filters={productSearchResult?.refinements}
                                        selectedFilters={updatedRefine}
                                        onClose={onClose}
                                    />
                                </Stack>
                                <Box>
                                    <SimpleGrid
                                        columns={[2, 2, 3, 3]}
                                        spacingX={{base: 2.5, md: 6}}
                                        spacingY={{base: 2.5, lg: 6}}
                                    >
                                        <>
                                            {isProductsLoading && (
                                                <ProductListsSkelton limit={searchParams.limit} />
                                            )}
                                        </>
                                        <>
                                            {!isHydrated() &&
                                            ((isRefetching && !isFetched) ||
                                                isProductsLoading ||
                                                !productSearchResult) ? (
                                                <ProductListsSkelton limit={searchParams.limit} />
                                            ) : products?.length > 0 ? (
                                                products.map((productSearchItem, productIdx) => {
                                                    const productId = productSearchItem.productId
                                                    const isInWishlist =
                                                        !!wishlist?.customerProductListItems?.find(
                                                            (item) => item.productId === productId
                                                        )
                                                    const billBoardTileData =
                                                        categoryBillboardData[
                                                            `tile-${productIdx + 1}`
                                                        ]
                                                    return (
                                                        <>
                                                            {sitePrefData?.enableCategoryBillBoards &&
                                                                billBoardTileData && (
                                                                    <ContentRenderer
                                                                        fontFamily="Montserrat"
                                                                        contentIds={`${billBoardTileData.contentId}:category:${category?.id}`}
                                                                        sx={
                                                                            billBoardTileData.size ==
                                                                            '21'
                                                                                ? size2x1
                                                                                : size1x1
                                                                        }
                                                                        gridColumnEnd={
                                                                            billBoardTileData.size ==
                                                                            '21'
                                                                                ? 'span 2'
                                                                                : ''
                                                                        }
                                                                        validateEmptyHtml
                                                                    />
                                                                )}
                                                            <ProductTile
                                                                data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                                                key={productSearchItem.productId}
                                                                product={productSearchItem}
                                                                category={category}
                                                                previousSearchParams={
                                                                    previousSearchParams
                                                                }
                                                                enableFavourite={false}
                                                                isFavourite={isInWishlist}
                                                                isRefreshingData={
                                                                    isRefetching && isFetched
                                                                }
                                                                imageViewType={
                                                                    PRODUCT_LIST_IMAGE_VIEW_TYPE
                                                                }
                                                                selectableAttributeId={
                                                                    PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID
                                                                }
                                                                onClick={() => {
                                                                    setProductDataForClick(
                                                                        productSearchItem
                                                                    )
                                                                    if (searchQuery) {
                                                                        einstein.sendClickSearch(
                                                                            searchQuery,
                                                                            productSearchItem
                                                                        )
                                                                    } else if (category) {
                                                                        einstein.sendClickCategory(
                                                                            category,
                                                                            productSearchItem
                                                                        )
                                                                    }
                                                                }}
                                                                onFavouriteToggle={(
                                                                    toBeFavourite
                                                                ) => {
                                                                    const action = toBeFavourite
                                                                        ? addItemToWishlist
                                                                        : removeItemFromWishlist
                                                                    return action(productSearchItem)
                                                                }}
                                                                dynamicImageProps={{
                                                                    widths: [
                                                                        '50vw',
                                                                        '50vw',
                                                                        '20vw',
                                                                        '20vw',
                                                                        '25vw'
                                                                    ]
                                                                }}
                                                                onVisibilityChange={
                                                                    handleVisibilityChange
                                                                }
                                                                isGiftCardCategory={
                                                                    category?.id === 'gift-cards'
                                                                }
                                                            />
                                                        </>
                                                    )
                                                })
                                            ) : !isLoading && !productSearchResult?.hits ? (
                                                <Box gridColumn="1 / -1">
                                                    <Box sx={adjustFiltersStyles}>
                                                        <InformationIcon
                                                            sx={informationIconStyles}
                                                        />
                                                        <Text as="span">
                                                            <FormattedMessage
                                                                defaultMessage="Looks like we couldn’t find a match for those filters. Try adjusting your selections or explore a different category to continue your search."
                                                                id="product_list.adjust.filters.info"
                                                            />
                                                        </Text>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <ProductListsSkelton limit={searchParams.limit} />
                                            )}
                                            {isNextPageLoading && (
                                                <ProductListsSkelton limit={searchParams.limit} />
                                            )}
                                        </>
                                    </SimpleGrid>

                                    {/* Footer */}

                                    {products?.length &&
                                        productSearchResult?.total >
                                            (limitSizePrefData?.productTileFetchLimit ||
                                                DEFAULT_SEARCH_PARAMS.limit) && (
                                            <Flex
                                                justifyContent="center"
                                                paddingTop={{base: '10', md: '12'}}
                                                marginRight={{lg: pxToRem(280)}}
                                            >
                                                {
                                                    <Pagination
                                                        currentURL={basePath}
                                                        urls={pageUrls}
                                                    />
                                                }
                                            </Flex>
                                        )}
                                </Box>
                            </Grid>
                        )}

                    {/* Related Articles */}
                    {isSearch && allArticles.length > 0 && !showNoSearchResults && (
                        <RelatedArticles
                            relatedArticlesData={allArticles}
                            onLoadMore={loadMoreArticles}
                            showLoadMore={showLoadMore}
                            articlesCount={relatedArticlesData?.total}
                        />
                    )}
                    <Box sx={plpAboveFooter} dangerouslySetInnerHTML={{__html: response}} />
                </>
            )}
            {/* Modal for filter options on mobile */}
            {!isOpenStoreSelector && (
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    size="full"
                    motionPreset="slideInBottom"
                    scrollBehavior="inside"
                >
                    <ModalOverlay />
                    <ModalContent top={0} marginTop={0}>
                        <ModalHeader pb={0}>
                            <Button variant="red-link-uline" onClick={onClose} marginRight="auto">
                                <FormattedMessage
                                    defaultMessage="<< Hide Filter & Sort"
                                    id="product_list.button.hide_filter_and_sort"
                                />
                            </Button>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody py={4}>
                            {filtersLoading && <LoadingSpinner />}
                            <Box my={4}>
                                <SelectedRefinements
                                    filters={productSearchResult?.refinements}
                                    toggleFilter={toggleFilter}
                                    handleReset={resetFilters}
                                    selectedFilterValues={productSearchResult?.selectedRefinements}
                                />
                            </Box>
                            <Box>
                                <Sort
                                    sortUrls={sortUrls}
                                    productSearchResult={productSearchResult}
                                    basePath={basePath}
                                    defaultSort={searchParams.sort}
                                />
                            </Box>

                            {/* available at my store toggle button */}
                            <FormControl display="flex" alignItems="center" marginTop={pxToRem(20)}>
                                <Switch
                                    id="store-available_m"
                                    isChecked={isFilterStoreAvailable}
                                    onChange={filterStoreAvailable}
                                />
                                <FormLabel
                                    sx={availableAtStoreStyle}
                                    htmlFor="store-available_m"
                                    fontSize={pxToRem(12)}
                                    ml={pxToRem(10)}
                                    mb="0"
                                >
                                    {intl.formatMessage({
                                        id: 'product_list.availableAtMyStore.label',
                                        defaultMessage: 'Available At My Store'
                                    })}
                                </FormLabel>
                            </FormControl>
                            {/* avaialble at store start here */}
                            <Box display={'flex'} alignItems="flex-start" marginTop={pxToRem(10)}>
                                {/* selected store details */}
                                <StoreDetails isPLP={true} />
                                {/* avaialable at store ends here */}
                                {/* change store button */}
                                <Button
                                    variant="red-link-uline"
                                    onClick={openStoreModal}
                                    mt={pxToRem(2)}
                                    mb={pxToRem(15)}
                                    fontWeight={500}
                                    fontSize={{base: '10px', md: '12px'}}
                                    color="cav_calltoaction_darkred"
                                    lineHeight="normal"
                                    letterSpacing={pxToRem(1)}
                                >
                                    {storeDetails && Object.keys(storeDetails)?.length
                                        ? intl.formatMessage({
                                              id: 'product_list.store.change.label',
                                              defaultMessage: 'Change'
                                          })
                                        : intl.formatMessage({
                                              id: 'product_list.store.selectSore.label',
                                              defaultMessage: 'Select a store'
                                          })}
                                </Button>
                            </Box>

                            <Refinements
                                toggleFilter={toggleFilter}
                                filters={productSearchResult?.refinements}
                                selectedFilters={updatedRefine}
                                excludedFilters={[]}
                                itemsBefore={
                                    category?.categories
                                        ? [
                                              <CategoryLinks
                                                  key="itemsBefore"
                                                  category={category}
                                                  onSelect={onClose}
                                              />
                                          ]
                                        : undefined
                                }
                                onClose={onClose}
                            />
                        </ModalBody>

                        <ModalFooter
                            // justify="space-between"
                            display="block"
                            width="full"
                            borderTop="1px solid"
                            borderColor="gray.100"
                            paddingBottom={10}
                        >
                            <Stack>
                                <Button width="full" variant="red" onClick={onClose}>
                                    {formatMessage(
                                        {
                                            id: 'product_list.modal.button.view_items',
                                            defaultMessage: 'View {prroductCount} items'
                                        },
                                        {
                                            prroductCount: productSearchResult?.total
                                        }
                                    )}
                                </Button>
                                <Button width="full" variant="white" onClick={resetFilters}>
                                    <FormattedMessage
                                        defaultMessage="Clear Filters"
                                        id="product_list.modal.button.clear_filters"
                                    />
                                </Button>
                            </Stack>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
            <Drawer
                placement="bottom"
                isOpen={sortOpen}
                onClose={() => setSortOpen(false)}
                size="sm"
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                isFullHeight={false}
                height="50%"
            >
                <DrawerOverlay />
                <DrawerContent marginTop={0}>
                    <DrawerHeader boxShadow="none">
                        <Text fontWeight="bold" fontSize="2xl">
                            <FormattedMessage
                                defaultMessage="Sort By"
                                id="product_list.drawer.title.sort_by"
                            />
                        </Text>
                    </DrawerHeader>
                    <DrawerCloseButton />
                    <DrawerBody>
                        {sortUrls.map((href, idx) => (
                            <Button
                                width="full"
                                onClick={() => {
                                    setSortOpen(false)
                                    history.push(href)
                                }}
                                fontSize={'md'}
                                key={idx}
                                marginTop={0}
                                variant="menu-link"
                            >
                                <Text
                                    as={
                                        selectedSortingOptionLabel?.label ===
                                            productSearchResult?.sortingOptions[idx]?.label && 'u'
                                    }
                                >
                                    {productSearchResult?.sortingOptions[idx]?.label}
                                </Text>
                            </Button>
                        ))}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.propTypes = {
    onAddToWishlistClick: PropTypes.func,
    onRemoveWishlistClick: PropTypes.func,
    category: PropTypes.object,
    staticContext: PropTypes.object,
    isLoading: PropTypes.bool
}

export default ProductList

const Sort = ({sortUrls, productSearchResult, basePath, defaultSort, ...otherProps}) => {
    const intl = useIntl()
    const history = useHistory()
    const location = useLocation()

    const {data: limitSizePrefData} = useSitePreference('productTileFetchLimit')

    const selectStyle = {
        textIndent: [pxToRem(68), pxToRem(68), pxToRem(85), pxToRem(85)],
        paddingLeft: pxToRem(10),
        paddingTop: pxToRem(1),
        '@supports (-moz-appearance: none)': {
            textIndent: [pxToRem(34), pxToRem(34), pxToRem(42), pxToRem(42)],
            paddingRight: pxToRem(72)
        }
    }

    let selectedValue = basePath
    if (defaultSort) {
        selectedValue = rebuildPathWithParams(`${location.pathname}${location.search}`, {
            sort: defaultSort,
            offset: 0
        })
    }

    // Datalayer setSortChange for selected sorting option
    const setSortChange = (sortOption) => {
        if (sortOption) {
            if (typeof window !== 'undefined' && window?.dataLayer) {
                window.dataLayer.push({
                    event: 'PLPsort',
                    value: sortOption
                })
            }
        }
    }

    return (
        <FormControl
            aria-label={intl.formatMessage({
                id: 'product_list.drawer.title.sort_by',
                defaultMessage: 'Sort By'
            })}
            data-testid="sf-product-list-sort"
            id="page_sort"
            width={['80%', '80%', '100%', '100%']}
            {...otherProps}
            position={'relative'}
            border={`${pxToRem(1)} solid black`}
            _focus={{
                border: `${pxToRem(1)} solid #015fcc`
            }}
        >
            <Text
                fontFamily={fonts.primary}
                fontSize={[pxToRem(12), pxToRem(12), pxToRem(14), pxToRem(14)]}
                fontWeight={[500, 500, 500, 500]}
                lineHeight={'normal'}
                letterSpacing={[pxToRem(1), pxToRem(1), pxToRem(2), pxToRem(2)]}
                textTransform={'uppercase'}
                position={'absolute'}
                top={[pxToRem(12), pxToRem(12), pxToRem(11), pxToRem(11)]}
                left={[pxToRem(10)]}
            >
                Sort By:
            </Text>
            <Select
                value={selectedValue}
                onChange={(event) => {
                    history.push(event?.target?.value)
                    setSortChange(
                        productSearchResult?.sortingOptions.find((option) =>
                            event.target.value.includes(`sort=${option.id}`)
                        )?.label
                    )
                }}
                height={10}
                borderRadius={0}
                textOverflow={'ellipsis'}
                whiteSpace={'nowrap'}
                border={'none'}
                _focus={{
                    border: 'none',
                    outline: 'none'
                }}
                fontFamily={fonts.primary}
                fontSize={[pxToRem(12), pxToRem(12), pxToRem(14), pxToRem(14)]}
                fontWeight={[500, 500, 500, 500]}
                lineHeight={'normal'}
                letterSpacing={[pxToRem(1), pxToRem(1), pxToRem(2), pxToRem(2)]}
                textTransform={'uppercase'}
                icon={
                    <Img
                        src={getAssetUrl('static/img/plp/down_arrow.svg')}
                        height={[pxToRem(15), pxToRem(15), pxToRem(20), pxToRem(20)]}
                    />
                }
                sx={selectStyle}
            >
                {sortUrls.map((href, index) => (
                    <option key={href} value={href}>
                        {intl.formatMessage(
                            {
                                id: 'plp.filter.sort.option',
                                defaultMessage: '{sortOption}'
                            },
                            {sortOption: productSearchResult?.sortingOptions[index]?.label}
                        )}
                    </option>
                ))}
            </Select>
        </FormControl>
    )
}

Sort.propTypes = {
    sortUrls: PropTypes.array,
    productSearchResult: PropTypes.object,
    basePath: PropTypes.string,
    defaultSort: PropTypes.string
}
